import React, { useEffect, useState } from 'react';
import { api, fmtNum } from '../lib/api';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Search, Pencil } from 'lucide-react';

const YEAR_OPTIONS = [2026, 2027, 2028, 2029, 2030];

export default function MasterReagen() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [yearA, setYearA] = useState(2026);
  const [yearB, setYearB] = useState(2027);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadMaster = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`/api/reagen?year_a=${yearA}&year_b=${yearB}`, { headers });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();
      setItems(Array.isArray(data) ? data : (data.data || []));
    } catch (err) {
      try {
        const fallbackData = await api.listReagen();
        setItems(Array.isArray(fallbackData) ? fallbackData : []);
      } catch (fallbackErr) {
        toast.error('Gagal memuat data reagen');
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMaster();
    /* eslint-disable-next-line */
  }, [yearA, yearB]);

  const filteredMaster = items.filter((r) =>
    r && r.nama_reagen && r.nama_reagen.toLowerCase().includes(q.toLowerCase())
  );

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        item_code: editing.item_code || null,
        qty_per_kit: editing.qty_per_kit === '' ? null : Number(editing.qty_per_kit),
        buffer_stock: editing.buffer_stock === '' ? null : Number(editing.buffer_stock),
        satuan: editing.satuan || 'Pcs',
      };
      await api.updateReagen(editing.id, body);
      toast.success('Reagen diperbarui');
      setEditing(null);
      loadMaster();
    } catch (e) {
      toast.error('Gagal menyimpan perubahan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* DAFTAR REAGEN */}
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Daftar master reagen ({items.length} item). Atribut: Qty/kit, rata-rata sampel dinamis, buffer stock, satuan.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 bg-muted/30 p-1 rounded-md border text-xs">
              <span className="text-muted-foreground font-medium pl-1">AVG:</span>
              <select
                value={yearA}
                onChange={(e) => setYearA(Number(e.target.value))}
                className="h-7 text-xs rounded border border-input bg-background px-2 font-medium cursor-pointer"
              >
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <span className="text-muted-foreground font-medium">vs</span>
              <select
                value={yearB}
                onChange={(e) => setYearB(Number(e.target.value))}
                className="h-7 text-xs rounded border border-input bg-background px-2 font-medium cursor-pointer"
              >
                {YEAR_OPTIONS.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                data-testid="master-reagen-search"
                placeholder="Cari reagen..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="h-9 w-[180px] sm:w-[200px] pl-8"
              />
            </div>
          </div>
        </div>

        <Card className="p-0">
          {loading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="master-reagen-table">
                <thead>
                  <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 text-left">Nama Reagen</th>
                    <th className="px-3 py-2 text-right">Qty/kit</th>
                    <th className="px-3 py-2 text-right">AVG {yearA}</th>
                    <th className="px-3 py-2 text-right">AVG {yearB}</th>
                    <th className="px-3 py-2 text-right">Buffer Stock</th>
                    <th className="px-3 py-2 text-left">Satuan</th>
                    <th className="px-3 py-2 text-center">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMaster.map((r) => (
                    <tr key={r.id} className="border-t hover:bg-muted/40">
                      <td className="px-4 py-2 font-medium">{r.nama_reagen}</td>
                      <td className="num px-3 py-2 text-right">{fmtNum(r.qty_per_kit)}</td>
                      <td className="num px-3 py-2 text-right font-semibold text-primary">
                        {fmtNum(r.avg_year_a)}
                      </td>
                      <td className="num px-3 py-2 text-right font-semibold text-primary">
                        {fmtNum(r.avg_year_b)}
                      </td>
                      <td className="num px-3 py-2 text-right">{fmtNum(r.buffer_stock)}</td>
                      <td className="px-3 py-2 text-left text-muted-foreground">{r.satuan}</td>
                      <td className="px-3 py-2 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`master-reagen-edit-button-${r.id}`}
                          onClick={() =>
                            setEditing({
                              ...r,
                              qty_per_kit: r.qty_per_kit ?? '',
                              buffer_stock: r.buffer_stock ?? '',
                              item_code: r.item_code ?? '',
                            })
                          }
                        >
                          <Pencil className="mr-1 h-3 w-3" /> Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* DIALOG EDIT MASTER REAGEN */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Reagen</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Nama Reagen</Label>
                <Input value={editing.nama_reagen} disabled className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Qty/kit</Label>
                  <Input
                    type="number"
                    data-testid="edit-qty-per-kit"
                    value={editing.qty_per_kit}
                    onChange={(e) => setEditing({ ...editing, qty_per_kit: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Buffer Stock</Label>
                  <Input
                    type="number"
                    data-testid="edit-buffer-stock"
                    value={editing.buffer_stock}
                    onChange={(e) => setEditing({ ...editing, buffer_stock: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Satuan</Label>
                  <Input
                    value={editing.satuan}
                    onChange={(e) => setEditing({ ...editing, satuan: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Item Code</Label>
                  <Input
                    value={editing.item_code}
                    onChange={(e) => setEditing({ ...editing, item_code: e.target.value })}
                    className="mt-1"
                    placeholder="(opsional)"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Batal
            </Button>
            <Button onClick={save} disabled={saving} data-testid="master-reagen-save-button">
              {saving ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
